import React, { useEffect, useState, useCallback, useRef } from 'react';
import axiosApi, { suppliers as api } from '../utils/api';
import { useT } from '../i18n/I18nContext';
import { PriorityBadge, PRIORITY_OPTIONS, NEGOTIATION_STATUS_OPTIONS, fmtDate } from '../utils/helpers';
import { useToast, ToastContainer } from '../hooks/useToast';

const EMPTY = { name:'',country:'China',city:'',contact_name:'',contact_title:'',contact_email:'',contact_whatsapp:'',contact_wechat:'',contact_phone:'',website:'',category:'',product_types:'',priority:'MEDIUM',negotiation_status:'New',handwritten_notes:'',notes:'' };

const PAGE_LIMIT = 20;

export default function Suppliers() {
  const t = useT();
  const [list, setList]           = useState([]);
  const [total, setTotal]         = useState(0);
  const [page, setPage]           = useState(1);
  const [pages, setPages]         = useState(1);
  const [selected, setSelected]   = useState(null);
  const [showForm, setShowForm]   = useState(false);
  const [editing, setEditing]     = useState(null);
  const [form, setForm]           = useState(EMPTY);
  const [saving, setSaving]         = useState(false);
  const [importing, setImporting]   = useState(false);
  const [importResult, setImportResult] = useState(null);
  const [filters, setFilters]       = useState({ search:'', category:'', priority:'' });
  const [pendingBizCard, setPendingBizCard] = useState(null);
  const [pendingFiles, setPendingFiles]     = useState([]);
  const importRef = useRef();
  const bizCardRef = useRef();
  const { toasts, toast } = useToast();

  const load = useCallback((pageOverride) => {
    const p = pageOverride !== undefined ? pageOverride : page;
    const params = {};
    if (filters.search)   params.search   = filters.search;
    if (filters.category) params.category = filters.category;
    if (filters.priority) params.priority = filters.priority;
    params.page  = p;
    params.limit = PAGE_LIMIT;
    api.list(params).then(data => {
      setList(data.rows);
      setTotal(data.total);
      setPage(data.page);
      setPages(data.pages);
    });
  }, [filters]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const categories = [...new Set(list.map(s => (s.category||'').split('/')[0].trim()).filter(Boolean))].sort();

  function openNew()    { setForm(EMPTY); setEditing(null); setShowForm(true); setSelected(null); setPendingBizCard(null); setPendingFiles([]); }
  function openEdit(s)  { setForm({...s, product_types: s.product_types||'', notes: s.notes||''}); setEditing(s); setShowForm(true); setPendingBizCard(null); setPendingFiles([]); }
  function setField(k,v){ setForm(f => ({...f, [k]:v})); }

  async function handleSave() {
    if (!form.name) return alert(t('suppliers.name_required'));
    setSaving(true);
    try {
      let supplierId;
      if (editing) {
        await api.update(editing.id, form);
        supplierId = editing.id;
        toast(t('suppliers.saved'),'success');
      } else {
        const created = await api.create(form);
        supplierId = created.id;
        toast(t('suppliers.created'),'success');
      }

      if (pendingBizCard && supplierId) {
        const fd = new FormData();
        fd.append('files', pendingBizCard, pendingBizCard.name || 'business-card.jpg');
        await api.uploadFiles(supplierId, fd);
        setPendingBizCard(null);
      }

      if (pendingFiles.length > 0 && supplierId) {
        const fd = new FormData();
        pendingFiles.forEach(f => fd.append('files', f, f.name));
        await api.uploadFiles(supplierId, fd);
        setPendingFiles([]);
      }

      setShowForm(false); load(1);
      if (supplierId) { const updated = await api.get(supplierId); setSelected(updated); }
    } catch(e) { toast(t('common.error_saving'),'error'); }
    finally { setSaving(false); }
  }

  async function handleDelete(id) {
    if (!window.confirm(t('suppliers.delete.confirm'))) return;
    await api.delete(id); toast(t('suppliers.deleted'),'default'); setSelected(null); load(1);
  }

  async function handleImportXlsx(e) {
    const file = e.target.files[0]; if (!file) return;
    setImporting(true); setImportResult(null);
    try {
      const fd = new FormData(); fd.append('file', file);
      const { data } = await api.importXlsx(fd);
      setImportResult(data); toast(`${data.created} ${t('suppliers.created').toLowerCase()}, ${data.updated} ${t('suppliers.saved').toLowerCase()}`,'success'); load(1);
    } catch(err) { toast('Import failed: ' + (err.response?.data?.error||err.message),'error'); }
    finally { setImporting(false); e.target.value=''; }
  }

  async function handleBizCard(e) {
    const file = e.target.files[0]; if (!file) return;
    e.target.value = '';
    toast(t('suppliers.biz_card_reading'), 'default');
    try {
      const base64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload  = ev => resolve(ev.target.result.split(',')[1]);
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });
      const parsed = await axiosApi.post('/anthropic', {
        image_base64: base64,
        media_type: file.type || 'image/jpeg',
      }).then(r => r.data);

      setForm(f => ({...f, ...parsed}));
      setPendingBizCard(file);
      toast(t('suppliers.biz_card_success'), 'success');
    } catch(err) {
      toast(t('suppliers.biz_card_error') + (err.response?.data?.error || err.message), 'error');
    }
  }

  async function handleFileUpload(supplierId, files) {
    const fd = new FormData();
    Array.from(files).forEach(f => fd.append('files', f));
    const { data } = await api.uploadFiles(supplierId, fd);
    const updated = await api.get(supplierId);
    setSelected(updated);
    toast(t('suppliers.file_uploaded').replace('{n}', data.length), 'success');
  }

  async function handleDeleteFile(fileId) {
    await api.deleteFile(fileId);
    if (selected) { const updated = await api.get(selected.id); setSelected(updated); }
    toast(t('suppliers.file_removed'),'default');
  }

  if (selected && !showForm) {
    return <SupplierDetail supplier={selected} onBack={() => setSelected(null)} onEdit={() => openEdit(selected)} onDelete={() => handleDelete(selected.id)} onFileUpload={handleFileUpload} onDeleteFile={handleDeleteFile} toast={toast} />;
  }

  return (
    <div className="page">
      <div className="page-header">
        <div>
          <div className="page-title">🏭 {t('suppliers.title')}</div>
          <div className="page-subtitle">{total} {t('suppliers.title').toLowerCase()}{pages > 1 ? ` · ${page} ${t('pagination.of')} ${pages}` : ''}</div>
        </div>
        <div className="action-group">
          <input ref={importRef} type="file" accept=".xlsx,.xls" style={{display:'none'}} onChange={handleImportXlsx} />
          <button className="btn btn-ghost btn-sm" onClick={()=>importRef.current?.click()} disabled={importing}>
            {importing ? t('suppliers.import.loading') : t('suppliers.import.btn')}
          </button>
          <button className="btn btn-primary" onClick={openNew}>{t('suppliers.new')}</button>
        </div>
      </div>

      {importResult && (
        <div style={{background:'var(--green-light)',border:'1px solid #a3d9b5',borderRadius:'var(--radius)',padding:'12px 16px',marginBottom:16,fontSize:13,color:'var(--green)'}}>
          ✅ {importResult.created} {t('suppliers.created').replace('!','').toLowerCase()} · {importResult.updated} {t('suppliers.saved').replace('!','').toLowerCase()} · {importResult.skipped} ignorados
          <button style={{marginLeft:12,background:'none',border:'none',cursor:'pointer',color:'var(--green)'}} onClick={()=>setImportResult(null)}>✕</button>
        </div>
      )}

      <div className="filters-bar">
        <input placeholder={t('suppliers.filter.search')} value={filters.search} onChange={e=>{setPage(1);setFilters(f=>({...f,search:e.target.value}));}} />
        <select value={filters.category} onChange={e=>{setPage(1);setFilters(f=>({...f,category:e.target.value}));}}>
          <option value="">{t('suppliers.filter.all_categories')}</option>
          {categories.map(c=><option key={c}>{c}</option>)}
        </select>
        <select value={filters.priority} onChange={e=>{setPage(1);setFilters(f=>({...f,priority:e.target.value}));}}>
          <option value="">{t('suppliers.filter.all_priorities')}</option>
          {PRIORITY_OPTIONS.map(p=><option key={p}>{p}</option>)}
        </select>
        {(filters.search||filters.category||filters.priority) && (
          <button className="btn btn-ghost btn-sm" onClick={()=>{setPage(1);setFilters({search:'',category:'',priority:''});}}>{t('filter.clear')}</button>
        )}
      </div>

      <div className="card" style={{padding:0}}>
        {list.length === 0 ? (
          <div className="empty-state"><div className="empty-icon">🏭</div><div className="empty-title">{t('suppliers.empty')}</div></div>
        ) : (
          <div className="table-wrap" style={{border:'none',borderRadius:'var(--radius-lg)'}}>
            <table>
              <thead><tr>
                <th>{t('suppliers.col.company')}</th>
                <th>{t('col.category')}</th>
                <th>{t('suppliers.col.products')}</th>
                <th>{t('suppliers.col.contact')}</th>
                <th>{t('suppliers.col.priority')}</th>
                <th>{t('suppliers.col.negotiation_status')}</th>
                <th>{t('suppliers.col.country')}</th>
                <th></th>
              </tr></thead>
              <tbody>
                {list.map(s => (
                  <tr key={s.id} onClick={()=>setSelected(s)}>
                    <td className="bold">{s.name}</td>
                    <td><span className="tag">{s.category||'—'}</span></td>
                    <td style={{fontSize:12,color:'var(--gray-500)',maxWidth:160,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{s.product_types||'—'}</td>
                    <td>{s.contact_name||'—'}</td>
                    <td><PriorityBadge priority={s.priority} /></td>
                    <td><span className="tag">{s.negotiation_status||'New'}</span></td>
                    <td className="text-muted">{s.country}</td>
                    <td onClick={e=>e.stopPropagation()}>
                      <button className="btn btn-ghost btn-sm" onClick={()=>openEdit(s)}>✏️</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      {pages > 1 && (
        <div style={{display:'flex',alignItems:'center',justifyContent:'center',gap:12,marginTop:16}}>
          <button className="btn btn-ghost btn-sm" onClick={()=>{const p=page-1;setPage(p);load(p);}} disabled={page<=1}>{t('pagination.prev')}</button>
          <span style={{fontSize:13,color:'var(--gray-500)'}}>{page} {t('pagination.of')} {pages}</span>
          <button className="btn btn-ghost btn-sm" onClick={()=>{const p=page+1;setPage(p);load(p);}} disabled={page>=pages}>{t('pagination.next')}</button>
        </div>
      )}

      {showForm && (
        <SupplierForm form={form} setField={setField} editing={editing} saving={saving}
          bizCardRef={bizCardRef} onBizCard={handleBizCard}
          pendingBizCard={pendingBizCard}
          pendingFiles={pendingFiles} setPendingFiles={setPendingFiles}
          onSave={handleSave} onClose={()=>{setShowForm(false);setEditing(null);setPendingBizCard(null);setPendingFiles([]);}} />
      )}
      <input ref={bizCardRef} type="file" accept="image/*" style={{display:'none'}} onChange={handleBizCard} />
      <ToastContainer toasts={toasts} />
    </div>
  );
}

function SupplierForm({ form, setField, editing, saving, bizCardRef, onBizCard, pendingBizCard, pendingFiles, setPendingFiles, onSave, onClose }) {
  const t = useT();
  const attachRef = useRef();

  function handleAttachFiles(e) {
    const files = Array.from(e.target.files);
    if (!files.length) return;
    setPendingFiles(prev => [...prev, ...files]);
    e.target.value = '';
  }

  function removeFile(idx) {
    setPendingFiles(prev => prev.filter((_,i) => i !== idx));
  }

  return (
    <div className="modal-overlay" onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div className="modal modal-lg">
        <div className="modal-header">
          <div className="modal-title">{editing ? t('suppliers.form.title.edit') : t('suppliers.form.title.new')}</div>
          <div className="gap-8">
            <button className="btn btn-ghost btn-sm" onClick={()=>bizCardRef.current?.click()}>{t('suppliers.form.scan_biz')}</button>
            <button className="btn btn-ghost btn-sm btn-icon" onClick={onClose}>✕</button>
          </div>
        </div>
        <div className="modal-body">
          <div className="section-title">{t('suppliers.form.company_section')}</div>
          <div className="form-grid" style={{marginBottom:20}}>
            <div className="form-group full"><label>{t('suppliers.form.company_name')}</label><input value={form.name} onChange={e=>setField('name',e.target.value)} placeholder="Jiaxing Sky Fashion Co., Ltd" className="highlight" /></div>
            <div className="form-group"><label>{t('suppliers.form.country')}</label><input value={form.country} onChange={e=>setField('country',e.target.value)} placeholder="China" /></div>
            <div className="form-group"><label>{t('suppliers.form.city')}</label><input value={form.city} onChange={e=>setField('city',e.target.value)} placeholder="Jiaxing, Zhejiang" /></div>
            <div className="form-group"><label>{t('suppliers.form.website')}</label><input value={form.website} onChange={e=>setField('website',e.target.value)} placeholder="www.supplier.com" /></div>
            <div className="form-group"><label>{t('suppliers.form.category')}</label><input value={form.category} onChange={e=>setField('category',e.target.value)} placeholder="Roupas Prontas, Tecidos..." /></div>
            <div className="form-group full"><label>{t('suppliers.form.product_types')}</label><input value={form.product_types} onChange={e=>setField('product_types',e.target.value)} placeholder="Jaquetas acolchoadas, casacos híbridos..." /></div>
          </div>

          <div className="section-title">{t('suppliers.form.contact_section')}</div>
          <div className="form-grid" style={{marginBottom:20}}>
            <div className="form-group"><label>{t('suppliers.form.contact_name')}</label><input value={form.contact_name} onChange={e=>setField('contact_name',e.target.value)} placeholder="Wang Lei" /></div>
            <div className="form-group"><label>{t('suppliers.form.contact_title')}</label><input value={form.contact_title} onChange={e=>setField('contact_title',e.target.value)} placeholder="Sales Manager" /></div>
            <div className="form-group"><label>{t('col.email')}</label><input type="email" value={form.contact_email} onChange={e=>setField('contact_email',e.target.value)} placeholder="contact@supplier.com" /></div>
            <div className="form-group"><label>WhatsApp</label><input value={form.contact_whatsapp} onChange={e=>setField('contact_whatsapp',e.target.value)} placeholder="+86 138 0000 0000" /></div>
            <div className="form-group"><label>WeChat ID</label><input value={form.contact_wechat} onChange={e=>setField('contact_wechat',e.target.value)} placeholder="wechat_id" /></div>
            <div className="form-group"><label>{t('suppliers.form.phone')}</label><input value={form.contact_phone} onChange={e=>setField('contact_phone',e.target.value)} placeholder="+86-573-0000-0000" /></div>
          </div>

          <div className="section-title">{t('suppliers.form.classification_section')}</div>
          <div className="form-grid" style={{marginBottom:20}}>
            <div className="form-group">
              <label>{t('suppliers.col.priority')}</label>
              <select value={form.priority} onChange={e=>setField('priority',e.target.value)} className="highlight">
                <option value="HIGH">🔴 HIGH</option>
                <option value="MEDIUM">🟡 MEDIUM</option>
                <option value="LOW">⚪ LOW</option>
              </select>
            </div>
            <div className="form-group">
              <label>{t('suppliers.col.negotiation_status')}</label>
              <select value={form.negotiation_status} onChange={e=>setField('negotiation_status',e.target.value)}>
                {NEGOTIATION_STATUS_OPTIONS.map(s=><option key={s}>{s}</option>)}
              </select>
            </div>
            <div className="form-group full"><label>{t('suppliers.form.handwritten_notes')}</label><textarea value={form.handwritten_notes} onChange={e=>setField('handwritten_notes',e.target.value)} placeholder="..." rows={2} /></div>
            <div className="form-group full"><label>{t('suppliers.form.general_obs')}</label><textarea value={form.notes} onChange={e=>setField('notes',e.target.value)} placeholder="30 years exp, 4,000 employees, 7M pcs/year..." rows={3} /></div>
          </div>
        </div>
          <div className="section-title">{t('suppliers.form.attachments_section')}</div>
          <div style={{marginBottom:16}}>
            <input ref={attachRef} type="file" multiple accept="image/*,.pdf,.xlsx,.xls,.doc,.docx" style={{display:'none'}} onChange={handleAttachFiles} />

            {pendingBizCard && (
              <div style={{display:'flex',alignItems:'center',gap:8,background:'var(--gray-50)',borderRadius:'var(--radius)',padding:'8px 12px',marginBottom:8,fontSize:13}}>
                🪪 <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{pendingBizCard.name}</span>
                <span style={{fontSize:11,color:'var(--gray-400)'}}>business card</span>
              </div>
            )}

            {pendingFiles.map((f,i) => (
              <div key={i} style={{display:'flex',alignItems:'center',gap:8,background:'var(--gray-50)',borderRadius:'var(--radius)',padding:'8px 12px',marginBottom:6,fontSize:13}}>
                📄 <span style={{flex:1,overflow:'hidden',textOverflow:'ellipsis',whiteSpace:'nowrap'}}>{f.name}</span>
                <button onClick={()=>removeFile(i)} style={{background:'none',border:'none',cursor:'pointer',color:'var(--gray-400)',fontSize:14,lineHeight:1}}>✕</button>
              </div>
            ))}

            <button className="btn btn-ghost btn-sm" style={{width:'100%',marginTop:4}} onClick={()=>attachRef.current?.click()}>
              {t('suppliers.form.add_attachments')}
            </button>
          </div>

        <div className="modal-footer">
          <button className="btn btn-ghost" onClick={onClose}>{t('btn.cancel')}</button>
          <button className="btn btn-primary" onClick={onSave} disabled={saving}>
            {saving ? t('suppliers.form.saving') : editing ? t('suppliers.form.save_edit') : t('suppliers.form.save_new')}
          </button>
        </div>
      </div>
    </div>
  );
}

function SupplierDetail({ supplier: s, onBack, onEdit, onDelete, onFileUpload, onDeleteFile, toast }) {
  const t = useT();
  const fileRef = useRef();
  const images = (s.files||[]).filter(f=>f.file_type==='image');
  const docs    = (s.files||[]).filter(f=>f.file_type==='document');

  return (
    <div className="page">
      <div className="page-header">
        <div className="gap-8">
          <button className="btn btn-ghost btn-sm" onClick={onBack}>{t('btn.back')}</button>
          <div>
            <div className="page-title">{s.name}</div>
            <div className="page-subtitle">{s.city&&`${s.city} · `}{s.country}</div>
          </div>
        </div>
        <div className="action-group">
          <PriorityBadge priority={s.priority} />
          <button className="btn btn-ghost btn-sm" onClick={onEdit}>✏️ {t('btn.edit')}</button>
          <button className="btn btn-danger btn-sm" onClick={onDelete}>🗑️</button>
        </div>
      </div>

      <div className="detail-grid">
        <div className="card">
          <div className="card-header"><div className="card-title">{t('suppliers.detail.contact_card')}</div></div>
          <div className="card-body">
            {s.contact_name && <div style={{fontWeight:700,fontSize:15,color:'var(--pantone)',marginBottom:2}}>{s.contact_name}</div>}
            {s.contact_title && <div style={{fontSize:12,color:'var(--gray-500)',marginBottom:10}}>{s.contact_title}</div>}
            {s.contact_email && <div style={{fontSize:13,marginBottom:4}}>✉️ <a href={`mailto:${s.contact_email}`} style={{color:'var(--blue)'}}>{s.contact_email}</a></div>}
            {s.contact_whatsapp && <div style={{fontSize:13,marginBottom:4}}>💬 {s.contact_whatsapp}</div>}
            {s.contact_wechat && <div style={{fontSize:13,marginBottom:4}}>💚 {s.contact_wechat}</div>}
            {s.contact_phone && <div style={{fontSize:13}}>📞 {s.contact_phone}</div>}
            {s.website && <div style={{fontSize:13,marginTop:6}}><a href={s.website.startsWith('http')?s.website:`https://${s.website}`} target="_blank" rel="noreferrer" style={{color:'var(--blue)'}}>🌐 {s.website}</a></div>}
          </div>
        </div>
        <div className="card">
          <div className="card-header"><div className="card-title">{t('suppliers.detail.classification_card')}</div></div>
          <div className="card-body">
            <div style={{display:'flex',flexWrap:'wrap',gap:10,marginBottom:12}}>
              {s.category && <span className="tag">📂 {s.category}</span>}
              <span className={`priority-${s.priority||'MEDIUM'}`}>{s.priority||'MEDIUM'}</span>
              <span className="tag">{s.negotiation_status||'New'}</span>
            </div>
            {s.product_types && <div style={{fontSize:13,color:'var(--gray-600)'}}><strong>{t('suppliers.detail.products_label')}</strong> {s.product_types}</div>}
            {s.notes && <div style={{marginTop:10,fontSize:13,color:'var(--gray-600)',background:'var(--gray-50)',borderRadius:'var(--radius)',padding:10}}>{s.notes}</div>}
            {s.handwritten_notes && <div style={{marginTop:8,fontSize:12,color:'var(--gray-500)',fontStyle:'italic'}}>📝 {s.handwritten_notes}</div>}
          </div>
        </div>
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">{t('suppliers.detail.files_card')}</div>
          <div className="gap-8">
            <input ref={fileRef} type="file" multiple accept="image/*,.pdf,.xlsx,.xls,.doc,.docx" style={{display:'none'}}
              onChange={e=>{ if(e.target.files.length) { onFileUpload(s.id, e.target.files); e.target.value=''; } }} />
            <button className="btn btn-ghost btn-sm" onClick={()=>fileRef.current?.click()}>{t('suppliers.detail.upload_btn')}</button>
          </div>
        </div>
        <div className="card-body">
          {images.length > 0 && (
            <>
              <div style={{fontSize:12,fontWeight:600,color:'var(--gray-500)',marginBottom:8}}>{t('suppliers.detail.images_section')}</div>
              <div className="photo-grid" style={{marginBottom:16}}>
                {images.map(f=>(
                  <div key={f.id} style={{position:'relative'}}>
                    <a href={`/uploads/${f.filename}`} target="_blank" rel="noreferrer">
                      <img src={`/uploads/${f.filename}`} alt={f.original_name} className="photo-thumb" />
                    </a>
                    <button onClick={()=>onDeleteFile(f.id)} style={{position:'absolute',top:-5,right:-5,background:'var(--red)',color:'white',border:'none',borderRadius:'50%',width:17,height:17,fontSize:10,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center'}}>✕</button>
                  </div>
                ))}
              </div>
            </>
          )}
          {docs.length > 0 && (
            <>
              <div style={{fontSize:12,fontWeight:600,color:'var(--gray-500)',marginBottom:8}}>{t('suppliers.detail.docs_section')}</div>
              <div style={{display:'flex',flexWrap:'wrap',gap:8}}>
                {docs.map(f=>(
                  <div key={f.id} className="file-chip">
                    📄 <a href={`/uploads/${f.filename}`} target="_blank" rel="noreferrer" style={{color:'var(--pantone)'}}>{f.original_name}</a>
                    <button onClick={()=>onDeleteFile(f.id)}>✕</button>
                  </div>
                ))}
              </div>
            </>
          )}
          {!images.length && !docs.length && (
            <div className="upload-zone" onClick={()=>fileRef.current?.click()}>
              {t('suppliers.detail.upload_zone')}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
